import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Check, Send, Loader2 } from 'lucide-react';
import { authFetch } from '../../lib/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const settingsConfig = [
  { key: 'smtp_host', label: 'Serveur SMTP', placeholder: 'ssl0.ovh.net', type: 'text', description: 'Adresse du serveur SMTP (OVH: ssl0.ovh.net).' },
  { key: 'smtp_port', label: 'Port SMTP', placeholder: '587', type: 'text', description: 'Port du serveur (587 pour STARTTLS, 465 pour SSL).' },
  { key: 'smtp_user', label: 'Utilisateur SMTP', placeholder: 'hello@cloneia.fr', type: 'email', description: 'Votre adresse email complète.' },
  { key: 'smtp_password', label: 'Mot de passe SMTP', placeholder: 'Mot de passe de votre boite email', type: 'password', description: 'Le mot de passe de votre compte email.' },
  { key: 'smtp_sender', label: 'Email d\'envoi', placeholder: 'hello@cloneia.fr', type: 'email', description: 'L\'adresse depuis laquelle les emails sont envoyés (par défaut : utilisateur SMTP).' },
  { key: 'smtp_recipient', label: 'Email de réception', placeholder: 'hello@cloneia.fr', type: 'email', description: 'L\'adresse qui recevra les notifications de demandes de démo.' },
];

const AdminSettings = () => {
  const [values, setValues] = useState({});
  const [saved, setSaved] = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    authFetch(`${API_URL}/api/settings`)
      .then(res => res.json())
      .then(data => {
        const map = {};
        if (Array.isArray(data)) {
          data.forEach(s => { map[s.key] = s.value; });
        }
        setValues(map);
      })
      .catch(() => {});
  }, []);

  const saveSetting = async (key) => {
    await authFetch(`${API_URL}/api/settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, value: values[key] || '' }),
    });
    setSaved(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setSaved(prev => ({ ...prev, [key]: false })), 2000);
  };

  const saveAll = async () => {
    for (const setting of settingsConfig) {
      if (values[setting.key]) {
        await authFetch(`${API_URL}/api/settings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: setting.key, value: values[setting.key] }),
        });
      }
    }
    setSaved(settingsConfig.reduce((acc, s) => ({ ...acc, [s.key]: true }), {}));
    setTimeout(() => setSaved({}), 2000);
  };

  const testEmail = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await authFetch(`${API_URL}/api/settings/test-email`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setTestResult({ success: true, message: data.message });
      } else {
        setTestResult({ success: false, message: data.detail || 'Erreur inconnue' });
      }
    } catch {
      setTestResult({ success: false, message: 'Impossible de contacter le serveur' });
    }
    setTesting(false);
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-settings">
      <h1 className="text-2xl font-bold text-[#f5f5f5] mb-2">Paramètres</h1>
      <p className="text-[#71717a] text-sm mb-6">Configuration SMTP pour les notifications email des demandes de démo.</p>

      <div className="space-y-4 max-w-xl">
        {settingsConfig.map((setting) => (
          <div key={setting.key} className="p-5 rounded-xl border border-white/5 bg-[#0f0f17]" data-testid={`setting-${setting.key}`}>
            <label className="block text-[#f5f5f5] text-sm font-medium mb-1">{setting.label}</label>
            <p className="text-[#71717a] text-xs mb-3">{setting.description}</p>
            <div className="flex gap-2">
              <input
                type={setting.type}
                value={values[setting.key] || ''}
                onChange={(e) => setValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
                placeholder={setting.placeholder}
                className="flex-1 px-4 py-2 rounded-lg bg-[#1a1a2e] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6] placeholder-[#52525b]"
                data-testid={`setting-input-${setting.key}`}
              />
              <Button
                onClick={() => saveSetting(setting.key)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  saved[setting.key]
                    ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                    : 'bg-[#3b82f6] hover:bg-[#2563eb] text-white'
                }`}
                data-testid={`setting-save-${setting.key}`}
              >
                {saved[setting.key] ? <Check className="w-4 h-4" /> : 'Sauvegarder'}
              </Button>
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            onClick={saveAll}
            className="bg-[#3b82f6] hover:bg-[#2563eb] text-white px-6 py-2.5 rounded-lg font-medium"
            data-testid="settings-save-all"
          >
            Tout sauvegarder
          </Button>
          <Button
            onClick={testEmail}
            disabled={testing}
            className="bg-[#0f0f17] border border-white/10 hover:border-[#3b82f6]/50 text-[#f5f5f5] px-6 py-2.5 rounded-lg font-medium"
            data-testid="settings-test-email"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {testing ? 'Envoi en cours...' : 'Envoyer un email de test'}
          </Button>
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`p-4 rounded-xl text-sm ${
            testResult.success
              ? 'bg-green-500/10 border border-green-500/20 text-green-400'
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`} data-testid="test-email-result">
            {testResult.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
